import type {
  BoundingBox,
  Margins,
  NormalizedAnalysis,
  NormalizedFinding,
  SummaryCounts,
  UiRecord,
} from './types';

type UnknownRecord = Record<string, unknown>;

function objectValue(value: unknown): UnknownRecord {
  return value !== null && typeof value === 'object' ? (value as UnknownRecord) : {};
}

function firstValue(source: UnknownRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
}

function numberValue(source: UnknownRecord, keys: string[], fallback = 0): number {
  const value = firstValue(source, keys);
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function optionalNumber(source: UnknownRecord, keys: string[]): number | undefined {
  const value = firstValue(source, keys);
  if (value === undefined) return undefined;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function booleanValue(source: UnknownRecord, keys: string[]): boolean | undefined {
  const value = firstValue(source, keys);
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === 'true') return true;
  if (value === 0 || value === 'false') return false;
  return undefined;
}

function percentageValue(
  source: UnknownRecord,
  keys: string[],
  numerator: number,
  denominator: number,
): number {
  const provided = optionalNumber(source, keys);
  if (provided !== undefined) return provided <= 1 && provided > 0 ? provided * 100 : provided;
  return denominator > 0 ? (numerator / denominator) * 100 : 0;
}

function normalizeBoundingBox(
  value: unknown,
  width: number,
  height: number,
): BoundingBox | undefined {
  const box = objectValue(value);
  const x = numberValue(box, ['x', 'left', 'minX'], -1);
  const y = numberValue(box, ['y', 'top', 'minY'], -1);
  let boxWidth = numberValue(box, ['width'], -1);
  let boxHeight = numberValue(box, ['height'], -1);

  if (boxWidth < 0) {
    const maxX = optionalNumber(box, ['maxX', 'right']);
    if (maxX !== undefined && x >= 0) boxWidth = maxX - x + (box.maxX !== undefined ? 1 : 0);
  }
  if (boxHeight < 0) {
    const maxY = optionalNumber(box, ['maxY', 'bottom']);
    if (maxY !== undefined && y >= 0) boxHeight = maxY - y + (box.maxY !== undefined ? 1 : 0);
  }

  if (x < 0 || y < 0 || boxWidth <= 0 || boxHeight <= 0 || width <= 0 || height <= 0)
    return undefined;
  return {
    x: clamp(x, 0, width),
    y: clamp(y, 0, height),
    width: clamp(boxWidth, 0, width - x),
    height: clamp(boxHeight, 0, height - y),
  };
}

function normalizeMargins(
  source: UnknownRecord,
  bbox: BoundingBox | undefined,
  width: number,
  height: number,
): Margins {
  const marginsSource = objectValue(
    firstValue(source, ['transparentMargins', 'transparentPadding', 'margins', 'padding']),
  );
  const hasMarginObject = Object.keys(marginsSource).length > 0;
  if (hasMarginObject) {
    return {
      top: Math.max(0, Math.round(numberValue(marginsSource, ['top']))),
      right: Math.max(0, Math.round(numberValue(marginsSource, ['right']))),
      bottom: Math.max(0, Math.round(numberValue(marginsSource, ['bottom']))),
      left: Math.max(0, Math.round(numberValue(marginsSource, ['left']))),
    };
  }
  if (bbox) {
    return {
      top: Math.max(0, Math.round(bbox.y)),
      right: Math.max(0, Math.round(width - bbox.x - bbox.width)),
      bottom: Math.max(0, Math.round(height - bbox.y - bbox.height)),
      left: Math.max(0, Math.round(bbox.x)),
    };
  }
  return {
    top: Math.max(0, Math.round(numberValue(source, ['topMargin', 'marginTop']))),
    right: Math.max(0, Math.round(numberValue(source, ['rightMargin', 'marginRight']))),
    bottom: Math.max(0, Math.round(numberValue(source, ['bottomMargin', 'marginBottom']))),
    left: Math.max(0, Math.round(numberValue(source, ['leftMargin', 'marginLeft']))),
  };
}

function normalizeFindings(analysis: UnknownRecord): NormalizedFinding[] {
  const rawLabels = firstValue(analysis, ['labels', 'detections', 'tags']);
  const labels = Array.isArray(rawLabels) ? rawLabels : [];
  return labels.map((entry) => {
    if (typeof entry === 'string') {
      return {
        code: entry,
        suspected: entry.includes('SUSPECTED'),
      };
    }
    const label = objectValue(entry);
    const code = String(firstValue(label, ['code', 'type', 'label', 'id']) ?? 'UNKNOWN');
    const confidence = optionalNumber(label, ['confidence', 'score']);
    const reason = firstValue(label, ['reason', 'message', 'description']);
    return {
      code,
      confidence:
        confidence === undefined
          ? undefined
          : clamp(confidence <= 1 ? confidence * 100 : confidence, 0, 100),
      reason: typeof reason === 'string' ? reason : undefined,
      suspected: booleanValue(label, ['suspected']) ?? code.includes('SUSPECTED'),
    };
  });
}

function appendHeuristicFinding(
  findings: NormalizedFinding[],
  source: unknown,
  code: string,
): void {
  const detection = objectValue(source);
  const suspected =
    booleanValue(detection, ['suspected', 'detected', 'isDetected', 'match']) ?? false;
  if (!suspected) return;
  const confidence = optionalNumber(detection, ['confidence', 'score']);
  const rawReason = firstValue(detection, ['reason', 'reasons', 'message', 'description']);
  const reason = Array.isArray(rawReason)
    ? rawReason.filter((entry): entry is string => typeof entry === 'string').join(' ')
    : typeof rawReason === 'string'
      ? rawReason
      : undefined;
  const normalizedConfidence =
    confidence === undefined
      ? undefined
      : clamp(confidence <= 1 ? confidence * 100 : confidence, 0, 100);
  const existing = findings.find((finding) => finding.code === code);
  if (existing) {
    existing.suspected = true;
    existing.confidence = normalizedConfidence;
    existing.reason = reason;
    return;
  }
  findings.push({ code, confidence: normalizedConfidence, reason, suspected: true });
}

export function normalizeAnalysis(record: UiRecord): NormalizedAnalysis {
  const analysis = objectValue(record.analysis);
  const png = objectValue(firstValue(analysis, ['png', 'structure', 'pngStructure']));
  const pixels = objectValue(firstValue(analysis, ['pixels', 'statistics', 'pixelStatistics']));
  const width = Math.round(
    numberValue(png, ['width'], numberValue(pixels, ['width'], numberValue(analysis, ['width']))),
  );
  const height = Math.round(
    numberValue(
      png,
      ['height'],
      numberValue(pixels, ['height'], numberValue(analysis, ['height'])),
    ),
  );
  const totalPixels = Math.round(
    numberValue(pixels, ['totalPixels', 'pixelCount'], Math.max(0, width * height)),
  );
  const transparentPixels = Math.round(
    numberValue(pixels, [
      'transparentPixels',
      'fullyTransparentPixels',
      'alphaZeroPixels',
      'alpha0Count',
    ]),
  );
  const semiTransparentPixels = Math.round(
    numberValue(pixels, [
      'semiTransparentPixels',
      'partiallyTransparentPixels',
      'alphaPartialPixels',
    ]),
  );
  const opaquePixels = Math.round(
    numberValue(
      pixels,
      ['opaquePixels', 'fullyOpaquePixels', 'alpha255Pixels'],
      Math.max(0, totalPixels - transparentPixels - semiTransparentPixels),
    ),
  );
  const transparentPercentage = percentageValue(
    pixels,
    ['transparentPercentage', 'fullyTransparentPercentage', 'transparentPercent'],
    transparentPixels,
    totalPixels,
  );
  const semiTransparentPercentage = percentageValue(
    pixels,
    ['semiTransparentPercentage', 'partiallyTransparentPercentage', 'semiTransparentPercent'],
    semiTransparentPixels,
    totalPixels,
  );
  const bbox = normalizeBoundingBox(
    firstValue(pixels, [
      'boundingBox',
      'contentBoundingBox',
      'nonTransparentBoundingBox',
      'bounds',
    ]),
    width,
    height,
  );
  const margins = normalizeMargins(pixels, bbox, width, height);
  const findings = normalizeFindings(analysis);
  appendHeuristicFinding(
    findings,
    firstValue(analysis, ['solidBackground', 'backgroundDetection']),
    'SUSPECTED_SOLID_BACKGROUND',
  );
  appendHeuristicFinding(
    findings,
    firstValue(analysis, ['checkerboard', 'checkerboardDetection']),
    'SUSPECTED_BAKED_CHECKERBOARD',
  );
  const codes = new Set(findings.map((finding) => finding.code));
  const realTransparency =
    transparentPixels + semiTransparentPixels > 0 ||
    codes.has('REAL_TRANSPARENCY') ||
    codes.has('FULL_TRANSPARENCY_PRESENT') ||
    codes.has('SEMI_TRANSPARENT_PIXELS');
  const fullyOpaque =
    codes.has('FULLY_OPAQUE') ||
    (totalPixels > 0 && transparentPixels + semiTransparentPixels === 0);
  const suspected = findings.some(
    (finding) => finding.suspected || finding.code.includes('SUSPECTED'),
  );
  const hasPadding = Object.values(margins).some((margin) => margin > 0);
  const safeToTrim = record.status === 'ready' && Boolean(bbox) && realTransparency && hasPadding;
  const trimDisabledReason =
    record.status !== 'ready'
      ? 'pending'
      : !bbox || !realTransparency
        ? 'no-content'
        : !hasPadding
          ? 'no-padding'
          : null;
  const edgeCoverage = optionalNumber(pixels, [
    'edgeCoveragePercentage',
    'edgeNonTransparentPercentage',
    'edgeContentPercentage',
    'edgeNonTransparentRatio',
    'edgeContentRatio',
  ]);

  return {
    width,
    height,
    totalPixels,
    transparentPixels,
    semiTransparentPixels,
    opaquePixels,
    transparentPercentage: clamp(transparentPercentage, 0, 100),
    semiTransparentPercentage: clamp(semiTransparentPercentage, 0, 100),
    minAlpha: optionalNumber(pixels, ['minAlpha', 'minimumAlpha']),
    maxAlpha: optionalNumber(pixels, ['maxAlpha', 'maximumAlpha']),
    averageAlpha: optionalNumber(pixels, ['averageAlpha', 'meanAlpha']),
    edgeCoveragePercentage:
      edgeCoverage === undefined
        ? undefined
        : clamp(edgeCoverage <= 1 && edgeCoverage > 0 ? edgeCoverage * 100 : edgeCoverage, 0, 100),
    boundingBox: bbox,
    margins,
    realTransparency,
    fullyOpaque,
    suspected,
    safeToTrim,
    trimDisabledReason,
    bitDepth: optionalNumber(png, ['bitDepth']),
    colorType: firstValue(png, ['colorType', 'colorTypeName']) as number | string | undefined,
    hasTrns: booleanValue(png, ['hasTrns', 'hasTRNS', 'trnsPresent']),
    supportsAlpha: booleanValue(png, [
      'supportsTransparency',
      'supportsAlpha',
      'hasAlphaChannel',
      'hasAlpha',
      'alphaSupported',
    ]),
    analyzedAt: typeof analysis.analyzedAt === 'string' ? analysis.analyzedAt : undefined,
    durationMs: optionalNumber(analysis, ['durationMs', 'analysisDurationMs']),
    findings,
  };
}

export function summarize(records: UiRecord[]): SummaryCounts {
  return records.reduce<SummaryCounts>(
    (summary, record) => {
      if (record.status === 'ready') {
        const normalized = normalizeAnalysis(record);
        summary.analyzed += 1;
        if (normalized.realTransparency) summary.transparent += 1;
        if (normalized.fullyOpaque) summary.opaque += 1;
        if (normalized.suspected) summary.suspected += 1;
      } else if (record.status === 'error') {
        summary.errors += 1;
      } else {
        summary.inProgress += 1;
      }
      return summary;
    },
    {
      total: records.length,
      analyzed: 0,
      transparent: 0,
      opaque: 0,
      suspected: 0,
      errors: 0,
      inProgress: 0,
    },
  );
}

export function fileFingerprint(file: File): string {
  return `${file.name.toLocaleLowerCase()}|${file.size}|${file.lastModified}`;
}

export function isPngFile(file: File): boolean {
  const extensionMatches = file.name.toLocaleLowerCase().endsWith('.png');
  return extensionMatches && (file.type === '' || file.type === 'image/png');
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${unit}`;
}

export function formatPercent(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return 'N/A';
  if (value === 0 || value === 100) return `${Math.round(value)}%`;
  if (value < 0.1) return '<0.1%';
  return `${value.toFixed(value < 10 ? 2 : 1)}%`;
}

export function formatNumber(value: number | undefined, locale = 'en'): string {
  if (value === undefined || !Number.isFinite(value)) return 'N/A';
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDate(value: string | undefined, locale: string): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function findingLabel(code: string): string {
  return code
    .replaceAll('_', ' ')
    .toLocaleLowerCase()
    .replace(/^./, (character) => character.toLocaleUpperCase());
}

export function trimmedFileName(name: string): string {
  return `${name.replace(/\.png$/i, '')}.trimmed.png`;
}

export function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function extractBlob(value: unknown): Blob | undefined {
  if (value instanceof Blob) return value;
  const object = objectValue(value);
  for (const key of ['blob', 'file', 'png', 'output']) {
    if (object[key] instanceof Blob) return object[key];
  }
  return undefined;
}

function sanitizeReportValue(value: unknown, seen: WeakSet<object>): unknown {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return value;
  if (
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'symbol' ||
    typeof value === 'bigint'
  )
    return undefined;
  if (value instanceof Blob || value instanceof ArrayBuffer || ArrayBuffer.isView(value))
    return undefined;
  if (typeof ImageData !== 'undefined' && value instanceof ImageData) return undefined;
  if (Array.isArray(value))
    return value
      .map((entry) => sanitizeReportValue(entry, seen))
      .filter((entry) => entry !== undefined);
  if (typeof value === 'object') {
    if (seen.has(value)) return undefined;
    seen.add(value);
    const result: UnknownRecord = {};
    for (const [key, entry] of Object.entries(value as UnknownRecord)) {
      if (
        [
          'source',
          'sourceFile',
          'imageData',
          'pixelsData',
          'rgba',
          'buffer',
          'arrayBuffer',
        ].includes(key)
      )
        continue;
      const sanitized = sanitizeReportValue(entry, seen);
      if (sanitized !== undefined) result[key] = sanitized;
    }
    return result;
  }
  return undefined;
}

export function createJsonReport(records: UiRecord[]): string {
  const files = records
    .filter((record) => record.status === 'ready' && record.analysis !== undefined)
    .map((record) => ({
      file: {
        name: record.file.name,
        size: record.file.size,
        type: record.file.type || 'image/png',
        lastModified: record.file.lastModified,
      },
      analysis: sanitizeReportValue(record.analysis, new WeakSet()),
    }));
  return JSON.stringify(
    {
      tool: 'TrueAlpha',
      version: '0.1.0',
      exportedAt: new Date().toISOString(),
      files,
    },
    null,
    2,
  );
}

function csvCell(value: string | number | boolean): string {
  const text = String(value);
  return /[\n\r,"]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function createCsvReport(records: UiRecord[]): string {
  const headers = [
    'fileName',
    'fileSize',
    'width',
    'height',
    'realTransparency',
    'fullyTransparentPercent',
    'semiTransparentPercent',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'labels',
    'analyzedAt',
  ];
  const rows = records
    .filter((record) => record.status === 'ready')
    .map((record) => {
      const analysis = normalizeAnalysis(record);
      return [
        record.file.name,
        record.file.size,
        analysis.width,
        analysis.height,
        analysis.realTransparency,
        analysis.transparentPercentage,
        analysis.semiTransparentPercentage,
        analysis.margins.top,
        analysis.margins.right,
        analysis.margins.bottom,
        analysis.margins.left,
        analysis.findings.map((finding) => finding.code).join('|'),
        analysis.analyzedAt ?? '',
      ].map(csvCell);
    });
  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
